import React, {
  ButtonHTMLAttributes,
  ElementType,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  cloneElement,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import {
  SxProps,
  Theme,
} from '@mui/material/styles';
import isNil from 'lodash/isNil';
import {
  notEmptyString,
} from '@populateui/utils';
import DropdownMenuArrow, {
  ARROW_SIZE,
  PopperPlacement,
} from 'core/components/DropdownMenu/arrow';
import ToolTip from 'core/components/Tooltip';

export type DropdownMenuItem<TData = unknown> = {
  id: string;
  label?: string;
  onSelect?: (data?: TData) => void;
  icon?: ElementType;
  iconClassName?: string;
  disabled?: boolean;
  tooltip?: string;
  variant?: 'default' | 'danger';
  className?: string;
};

type Props<TData = unknown> = {
  trigger: ReactElement<ButtonHTMLAttributes<HTMLButtonElement>>;
  items: DropdownMenuItem<TData>[];
  data?: TData;
  openOnHover?: boolean;
  hoverCloseDelayMs?: number;
  offset?: [skidding: number, distance: number];
  paperClassName?: string;
  paperSx?: SxProps<Theme>;
  placement?: PopperPlacement;
  showArrow?: boolean;
  disableFlip?: boolean;
};

function getTriggerDisabled(trigger: ReactElement): boolean {
  return Boolean((trigger.props as { disabled?: boolean }).disabled);
}

const transformOriginByPlacement: Record<PopperPlacement, string> = {
  'bottom-start': 'top left',
  bottom: 'top center',
  'bottom-end': 'top right',
  'top-start': 'bottom left',
  top: 'bottom center',
  'top-end': 'bottom right',
  'left-start': 'right top',
  'left-end': 'right bottom',
  'right-start': 'left top',
  'right-end': 'left bottom',
};

const defaultOffset: [number, number] = [0, ARROW_SIZE + 5];

type DropdownMenuItemButtonProps<TData> = {
  item: DropdownMenuItem<TData>;
  data?: TData;
  onClose: () => void;
};

function DropdownMenuItemButton<TData>({
  item,
  data,
  onClose,
}: DropdownMenuItemButtonProps<TData>) {
  const label = item.label ?? item.id;
  const isDanger = item.variant === 'danger';
  const Icon = item.icon;
  const iconClass = item.iconClassName ?? '!w-4 !h-4 !text-inherit';

  return (
    <MenuItem
      disabled={item.disabled}
      onClick={(event) => {
        event.stopPropagation();
        onClose();
        item.onSelect?.(data);
      }}
      className={clsx(
        'action-menu-item',
        '!text-xs !font-semibold',
        'hover:!bg-blue-50 hover:!text-blue-600',
        '[&.Mui-focusVisible]:!outline-none [&.Mui-focusVisible]:!ring-2 [&.Mui-focusVisible]:!ring-blue-500',
        item.className,
        {
          '!text-red-600 hover:!bg-red-50 hover:!text-red-700': isDanger,
          '[&.Mui-focusVisible]:!ring-red-500': isDanger,
          '!opacity-60 !cursor-not-allowed': item.disabled,
        },
      )}
    >
      {isNil(Icon) ? null : (
        <ListItemIcon
          sx={{ minWidth: 28 }}
          className={clsx(
            '!min-w-0 !mr-2',
            { '!text-red-600': isDanger },
          )}
        >
          <Icon className={iconClass} />
        </ListItemIcon>
      )}
      <ListItemText
        primary={label}
        slotProps={{
          primary: { className: '!text-inherit !text-xs !font-semibold' },
        }}
      />
    </MenuItem>
  );
}

function DropdownMenuListItem<TData>({
  item,
  data,
  onClose,
}: DropdownMenuItemButtonProps<TData>) {
  return notEmptyString(item.tooltip) ? (
    <ToolTip title={item.tooltip} placement="left">
      <span className="inline-flex w-full">
        <DropdownMenuItemButton item={item} data={data} onClose={onClose} />
      </span>
    </ToolTip>
  ) : (
    <DropdownMenuItemButton item={item} data={data} onClose={onClose} />
  );
}

export default function DropdownMenu<TData = unknown>({
  trigger,
  items,
  data,
  openOnHover = false,
  hoverCloseDelayMs = 150,
  offset = defaultOffset,
  paperClassName,
  paperSx,
  placement = 'bottom-start',
  showArrow = true,
  disableFlip = false,
}: Props<TData>) {
  const menuId = useId();
  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const [open, setOpen] = useState(false);
  const [arrowElement, setArrowElement] = useState<HTMLSpanElement | null>(null);
  const triggerDisabled = useMemo(() => getTriggerDisabled(trigger), [trigger]);

  const closeTimerRef = useRef<number | null>(null);
  const openReasonRef = useRef<'hover' | 'click' | null>(null);

  const clearCloseTimer = () => {
    if (!isNil(closeTimerRef.current)) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const closeMenu = () => {
    clearCloseTimer();
    openReasonRef.current = null;
    setOpen(false);
  };

  const scheduleClose = () => {
    if (openReasonRef.current !== 'click') {
      clearCloseTimer();
      closeTimerRef.current = window.setTimeout(() => {
        openReasonRef.current = null;
        setOpen(false);
      }, hoverCloseDelayMs);
    }
  };

  return (
    <ClickAwayListener onClickAway={closeMenu}>
      <div className="inline-flex self-stretch overflow-visible relative">
        <span
          ref={anchorRef}
          className="inline-flex h-full"
          onMouseEnter={() => {
            if (openOnHover && !triggerDisabled) {
              openReasonRef.current = 'hover';
              clearCloseTimer();
              setOpen(true);
            }
          }}
          onMouseLeave={() => {
            if (openOnHover && open) {
              scheduleClose();
            }
          }}
        >
          {cloneElement(trigger, {
            type: trigger.props.type ?? 'button',
            disabled: trigger.props.disabled ?? triggerDisabled,
            className: clsx(trigger.props.className, 'no-focus-ring'),
            'aria-haspopup': 'menu',
            'aria-controls': open ? menuId : undefined,
            'aria-expanded': open ? 'true' : undefined,
            onClick: (event: MouseEvent<HTMLButtonElement>) => {
              trigger.props.onClick?.(event);
              if (!event.defaultPrevented && !triggerDisabled) {
                clearCloseTimer();
                openReasonRef.current = 'click';
                setOpen(true);
              }
            },
            onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => {
              trigger.props.onKeyDown?.(event);
              if (!event.defaultPrevented && !triggerDisabled) {
                if (event.key === 'Escape') {
                  event.preventDefault();
                  closeMenu();
                } else if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  clearCloseTimer();
                  openReasonRef.current = 'click';
                  setOpen(true);
                }
              }
            },
          })}
        </span>
        <Popper
          id={menuId}
          open={open}
          anchorEl={anchorRef.current}
          placement={placement}
          disablePortal={false}
          transition
          modifiers={[
            {
              name: 'offset',
              options: { offset },
            },
            {
              name: 'arrow',
              enabled: true,
              options: { element: arrowElement },
            },
            {
              name: 'flip',
              enabled: !disableFlip,
            },
          ]}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              closeMenu();
            }
          }}
          sx={{
            // Ensure this menu appears above sticky headers / panels.
            zIndex: (theme) => theme.zIndex.modal + 1,
          }}
        >
          {({ TransitionProps }) => (
            <Grow
              {...TransitionProps}
              timeout={400}
              style={{ transformOrigin: transformOriginByPlacement[placement] }}
            >
              <div style={{ position: 'relative' }}>
                {showArrow && (
                  <DropdownMenuArrow
                    placement={placement}
                    arrowRef={setArrowElement}
                  />
                )}
                <Paper
                  sx={paperSx}
                  className={clsx(
                    '!bg-white rounded-md overflow-hidden',
                    paperClassName,
                  )}
                  onMouseEnter={clearCloseTimer}
                  onMouseLeave={scheduleClose}
                >
                  <MenuList
                    dense
                    className="action-menu-list"
                    autoFocusItem={open && openReasonRef.current === 'click'}
                  >
                    {items.map((item) => (
                      <DropdownMenuListItem
                        key={item.id}
                        item={item}
                        data={data}
                        onClose={closeMenu}
                      />
                    ))}
                  </MenuList>
                </Paper>
              </div>
            </Grow>
          )}
        </Popper>
      </div>
    </ClickAwayListener>
  );
}
